import { CloseOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
    Button,
    DatePicker,
    Drawer,
    Input,
    message,
    Radio,
    Select,
    TimePicker,
} from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import Dragger from 'antd/lib/upload/Dragger';
import Helper from 'library/Helper';
import { uniqueId } from 'lodash';
import moment, { Moment } from 'moment';
import momentTimezone from 'moment-timezone';
import {
    fetchRescheduleHolidays,
    rescheduleConsignments,
    rescheduleConsignmentsBulk,
} from 'network/consignments.api';
import {
    BulkRescheduleRow,
    RescheduleConsignmentsBody,
    RescheduleHoliday,
} from 'network/consignment-api.types';
import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'theme/jss-types';
import { cancellationStyles } from './consignment-cancel.styles';
import Cross from '../../assets/cross';
import { useTranslation } from 'react-i18next';
import { ReduxStore } from 'reducers/redux.types';
import { CustomerRescheduleConfig } from 'types/master-data-types';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import useIsMobile from 'hooks/useIsMobile';
import {
    IHolidayDate,
    createDateCellRender,
} from 'utils/holidayUtils';

export type RescheduleAction = 'SELECTED' | 'BULK';

export type RescheduleConsignment = {
    reference_number: string;
    expected_delivery_date?: string | number | null;
};

const MAX_NOTES_LENGTH = 500;
const MAX_RESCHEDULE_DAYS = 365;
const BULK_DATE_FORMAT = 'DD-MM-YYYY';
const SLOT_CODE_BULK_HEADERS = ['reference_number', 'reschedule_date', 'slot_code', 'notes'];
const CUSTOM_SLOT_BULK_HEADERS = ['reference_number', 'reschedule_date', 'slot_start', 'slot_end', 'notes'];
// The backend expects HHmm, the same string format as org delivery_timeslots
const SLOT_TIME_FORMAT = 'HHmm';
const SLOT_TIME_DISPLAY_FORMAT = 'HH:mm';
const DATE_FORMAT = 'YYYY-MM-DD';
const HOLIDAY_TYPE_DAY_OFF = 'DAY_OFF';
// The customer portal reschedules deliveries only
const RESCHEDULE_TYPE_DELIVERY = 'delivery';
const EMPTY_RESCHEDULE_CONFIG: CustomerRescheduleConfig = {};

interface RescheduleProps extends StylesProps<ReturnType<typeof cancellationStyles>> {
    onClose: (reload: boolean, params?: any) => void;
    action: RescheduleAction;
    consignments: RescheduleConsignment[];
    rescheduleConfig: CustomerRescheduleConfig;
    timezone?: string;
}

// Slot times come as HHmm or HH:mm (same as org delivery_timeslots); HHmm strings compare in time order
const toSlotTime = (time: string) => String(time || '').replace(':', '');

// Excel date cells arrive as Date objects (cellDates), text cells as strings.
// Noon is used before taking the day so that sub-minute offsets in parsed dates cannot shift it.
const formatBulkDate = (value: any): string => {
    if (value instanceof Date) {
        return moment(value).add(12, 'hours').startOf('day').format(BULK_DATE_FORMAT);
    }
    return String(value ?? '').trim();
};

// Excel time cells arrive as Date objects on 1899-12-30; 30 seconds are added for the same sub-minute offsets.
// Text cells (10:30, 1030) are sent as typed; the backend normalises them to HHmm.
const formatBulkSlotTime = (value: any): string => {
    if (value instanceof Date) {
        return moment(value).add(30, 'seconds').format(SLOT_TIME_FORMAT);
    }
    return String(value ?? '').trim();
};

const toBulkRescheduleRow = (row: any): BulkRescheduleRow => {
    const slotCode = String(row.slot_code ?? '').trim();
    const slotStart = formatBulkSlotTime(row.slot_start);
    const slotEnd = formatBulkSlotTime(row.slot_end);
    const notes = String(row.notes ?? '').trim();
    return {
        consignmentNumber: String(row.reference_number ?? '').trim(),
        rescheduleDate: formatBulkDate(row.reschedule_date),
        ...(slotCode ? { slotCode } : {}),
        ...(slotStart ? { slotStart } : {}),
        ...(slotEnd ? { slotEnd } : {}),
        ...(notes ? { notes } : {}),
    };
};

const RescheduleModal = (props: RescheduleProps) => {
    const {
        classes,
        onClose,
        action,
        consignments,
        rescheduleConfig,
        timezone,
    } = props;
    const {
        delivery_timeslots: deliveryTimeslots = [],
        holidays,
        restrict_reschedule_before_edd: restrictBeforeEdd = false,
        reschedule_reasons: rescheduleReasons = [],
        allow_custom_timeslot: allowCustomTimeslot = false,
        use_holiday_master: useHolidayMaster = false,
        mandatory_reschedule_reason: mandatoryRescheduleReason = false,
    } = rescheduleConfig || EMPTY_RESCHEDULE_CONFIG;
    const bulkHeaders = allowCustomTimeslot ? CUSTOM_SLOT_BULK_HEADERS : SLOT_CODE_BULK_HEADERS;
    // Mandatory only where the org configures it, and only when there are reasons to choose from
    const isReasonRequired = mandatoryRescheduleReason && rescheduleReasons.length > 0;

    // Today and the current time are the organisation's, the same clock the backend checks against
    const orgTimezone = timezone || momentTimezone.tz.guess();
    const nowInOrg = momentTimezone().tz(orgTimezone);
    const todayInOrg = nowInOrg.format(DATE_FORMAT);
    const currentSlotTime = nowInOrg.format(SLOT_TIME_FORMAT);

    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const initialList = () => consignments.map((item) => ({ ...item, id: uniqueId() }));
    const [list, setList] = React.useState<(RescheduleConsignment & { id: string })[]>(initialList);
    const [rescheduleDate, setRescheduleDate] = React.useState<Moment | null>(null);
    const [slotCode, setSlotCode] = React.useState<string | undefined>();
    const [customSlot, setCustomSlot] = React.useState<[Moment, Moment] | null>(null);
    const [reasonId, setReasonId] = React.useState<string | undefined>();
    const [notes, setNotes] = React.useState<string>('');
    const [bulkRows, setBulkRows] = React.useState<BulkRescheduleRow[]>([]);
    const [masterHolidayMap, setMasterHolidayMap] = React.useState<Map<string, IHolidayDate>>(new Map());
    const fetchedMonthsRef = React.useRef<Set<string>>(new Set());
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    // Holiday Master holidays and day-offs depend on the destination, so they are only shown for a single CN;
    // the backend checks each CN when several are rescheduled together
    const holidayMasterReference = useHolidayMaster && list.length === 1 ? list[0].reference_number : null;

    const loadMasterHolidays = React.useCallback(async (month: Moment) => {
        const monthKey = month.format('YYYY-MM');
        const monthStart = month.clone().startOf('month').format(DATE_FORMAT);
        const monthEnd = month.clone().endOf('month').format(DATE_FORMAT);
        if (!holidayMasterReference || fetchedMonthsRef.current.has(monthKey) || monthEnd < todayInOrg) {
            return;
        }

        fetchedMonthsRef.current.add(monthKey);
        const response = await fetchRescheduleHolidays({
            reference_number: holidayMasterReference,
            start_date: monthStart < todayInOrg ? todayInOrg : monthStart,
            end_date: monthEnd,
        });
        if (!response.isSuccess) {
            fetchedMonthsRef.current.delete(monthKey);
            message.error(response.errorMessage);
            return;
        }

        setMasterHolidayMap((previousMap) => {
            const nextMap = new Map(previousMap);
            (response.data?.holidays || []).forEach((holiday: RescheduleHoliday) => {
                const isDayOff = holiday.holiday_type === HOLIDAY_TYPE_DAY_OFF;
                const holidayTypeKey = isDayOff ? 'reschedule_day_off' : 'reschedule_holiday';
                nextMap.set(holiday.date, {
                    date: holiday.date,
                    holidayName: holiday.holiday_name || t('reschedule_delivery_not_available'),
                    holidayType: t(holidayTypeKey),
                });
            });
            return nextMap;
        });
    }, [holidayMasterReference, todayInOrg, t]);

    React.useEffect(() => {
        fetchedMonthsRef.current = new Set();
        setMasterHolidayMap(new Map());
        loadMasterHolidays(moment());
    }, [loadMasterHolidays]);

    // Org-config holidays win over a Holiday Master entry for the same date
    const holidayMap = React.useMemo(() => {
        const map = new Map<string, IHolidayDate>(masterHolidayMap);
        (holidays || []).forEach((holiday) => {
            map.set(holiday.date, {
                date: holiday.date,
                holidayName: holiday.name || '',
                holidayType: t('reschedule_holiday'),
            });
        });
        return map;
    }, [holidays, masterHolidayMap, t]);

    // The EDD floor only applies when a single CN is being rescheduled; the backend checks each CN otherwise
    const eddMinDate = React.useMemo(() => {
        const edd = list.length === 1 ? list[0].expected_delivery_date : null;
        if (!restrictBeforeEdd || !edd) {
            return null;
        }
        const eddMoment = momentTimezone(edd).tz(orgTimezone);
        return eddMoment.isValid() ? eddMoment : null;
    }, [list, restrictBeforeEdd, orgTimezone]);

    const maxDate = nowInOrg.clone().add(MAX_RESCHEDULE_DAYS, 'days').format(DATE_FORMAT);
    // Calendar cells are compared as YYYY-MM-DD strings, so the browser timezone cannot shift a day
    const disabledDate = (current: Moment) => {
        if (!current) return false;
        const dateString = current.format(DATE_FORMAT);
        if (dateString < todayInOrg || dateString > maxDate || holidayMap.has(dateString)) {
            return true;
        }
        return Boolean(eddMinDate && dateString < eddMinDate.format(DATE_FORMAT));
    };

    const isRescheduleToday = Boolean(rescheduleDate && rescheduleDate.format(DATE_FORMAT) === todayInOrg);

    // Slot days are ISO weekdays (1 = Monday ... 7 = Sunday), same as the backend check.
    // On today, a slot that has already ended is not offered.
    const availableSlots = rescheduleDate
        ? deliveryTimeslots.filter((slot) => (slot.days || []).includes(rescheduleDate.isoWeekday())
            && !(isRescheduleToday && toSlotTime(slot.end) <= currentSlotTime))
        : [];

    const isCustomSlotInvalid = Boolean(customSlot && !customSlot[1].isAfter(customSlot[0], 'minute'));
    const isCustomSlotInPast = Boolean(customSlot && isRescheduleToday
        && customSlot[1].format(SLOT_TIME_FORMAT) <= currentSlotTime);

    const handleDateChange = (date: Moment | null) => {
        setRescheduleDate(date);
        setSlotCode(undefined);
        setCustomSlot(null);
    };

    const showResult = (response: any, failures: any[]) => {
        if (!response.isSuccess) {
            message.error(response.errorMessage);
        } else if (failures?.length) {
            onClose(true, { failures });
        } else {
            message.success(t('reschedule_success'));
            onClose(true);
        }
    };

    const handleSelectedReschedule = async () => {
        const body: RescheduleConsignmentsBody = {
            consignments: list.map((item) => item.reference_number),
            rescheduled_date: moment(rescheduleDate).format('YYYY-MM-DD'),
        };
        if (allowCustomTimeslot && customSlot) {
            body.delivery_timeslot_start = customSlot[0].format(SLOT_TIME_FORMAT);
            body.delivery_timeslot_end = customSlot[1].format(SLOT_TIME_FORMAT);
        } else if (slotCode) {
            body.delivery_timeslot = slotCode;
        }
        if (reasonId) {
            body.rescheduled_reason_id = reasonId;
        }
        if (notes.trim()) {
            body.notes = notes.trim();
        }
        const response = await rescheduleConsignments(body);
        showResult(response, response?.data?.failures);
    };

    const handleBulkReschedule = async () => {
        const response = await rescheduleConsignmentsBulk({ bulkRescheduleArray: bulkRows });
        const failures = (response?.data?.failures || []).map((failure: any) => ({
            ...failure,
            message: failure.message || (failure.row ? `Row ${failure.row}` : ''),
        }));
        showResult(response, failures);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        if (action === 'BULK') {
            await handleBulkReschedule();
        } else {
            await handleSelectedReschedule();
        }
        setSubmitting(false);
    };

    const isDisabled = () => {
        if (action === 'BULK') {
            return !bulkRows.length;
        }
        return !list.length || !rescheduleDate || isCustomSlotInvalid || isCustomSlotInPast
            || (isReasonRequired && !reasonId);
    };

    // Shown read-only, so the customer can see which task is being rescheduled
    const renderType = () => {
        return (
            <div className={classes.column}>
                <span className={classes.inputTitle}>
                    {t('reschedule_type')}
                </span>
                <Radio.Group value={RESCHEDULE_TYPE_DELIVERY} disabled>
                    <Radio value={RESCHEDULE_TYPE_DELIVERY}>{t('delivery')}</Radio>
                </Radio.Group>
            </div>
        );
    };

    const renderDate = () => {
        return (
            <div className={classes.column}>
                <span className={classes.inputTitle}>
                    {t('reschedule_date')}
                    *
                </span>
                <DatePicker
                    value={rescheduleDate}
                    onChange={handleDateChange}
                    onPanelChange={(value: Moment | null) => value && loadMasterHolidays(value)}
                    disabledDate={disabledDate}
                    dateRender={createDateCellRender(holidayMap)}
                    format="DD MMM YYYY"
                    className={classes.input}
                />
                {eddMinDate ? (
                    <span className={classes.uploadHint}>
                        {t('reschedule_min_date_hint', { date: eddMinDate.format('DD MMM YYYY') })}
                    </span>
                ) : null}
            </div>
        );
    };

    const handleCustomSlotChange = (values: any) => {
        setCustomSlot(values && values[0] && values[1] ? [values[0], values[1]] : null);
    };

    const renderCustomSlot = () => {
        return (
            <div className={classes.column}>
                <span className={classes.inputTitle}>
                    {t('time_slot')}
                </span>
                <TimePicker.RangePicker
                    value={customSlot}
                    format={SLOT_TIME_DISPLAY_FORMAT}
                    minuteStep={5}
                    disabled={!rescheduleDate}
                    placeholder={[t('reschedule_start_time'), t('reschedule_end_time')]}
                    onChange={handleCustomSlotChange}
                    className={classes.input}
                />
                {isCustomSlotInvalid ? (
                    <span className={classes.uploadHint}>
                        {t('reschedule_slot_end_after_start')}
                    </span>
                ) : null}
                {!isCustomSlotInvalid && isCustomSlotInPast ? (
                    <span className={classes.uploadHint}>
                        {t('reschedule_slot_in_past')}
                    </span>
                ) : null}
            </div>
        );
    };

    const renderSlot = () => {
        if (allowCustomTimeslot) {
            return renderCustomSlot();
        }
        if (!deliveryTimeslots.length) {
            return null;
        }
        return (
            <div className={classes.column}>
                <span className={classes.inputTitle}>
                    {t('time_slot')}
                </span>
                <Select
                    allowClear
                    value={slotCode}
                    disabled={!rescheduleDate}
                    placeholder={t('select_time_slot')}
                    notFoundContent={t('no_slot_available')}
                    onChange={(value: string | undefined) => setSlotCode(value)}
                    className={classes.input}
                >
                    {availableSlots.map((slot) => (
                        <Select.Option key={slot.code} value={slot.code}>
                            {`${slot.start} - ${slot.end}`}
                        </Select.Option>
                    ))}
                </Select>
            </div>
        );
    };

    const renderReason = () => {
        if (!rescheduleReasons.length) {
            return null;
        }
        return (
            <div className={classes.column}>
                <span className={classes.inputTitle}>
                    {t('reason')}
                    {isReasonRequired ? '*' : null}
                </span>
                <Select
                    allowClear
                    value={reasonId}
                    placeholder={t('select_reason')}
                    onChange={(value: string | undefined) => setReasonId(value)}
                    className={classes.input}
                >
                    {rescheduleReasons.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                            {item.reason}
                        </Select.Option>
                    ))}
                </Select>
            </div>
        );
    };

    const renderNotes = () => {
        return (
            <div className={classes.column}>
                <span className={classes.inputTitle}>
                    {t('notes')}
                </span>
                <Input.TextArea
                    value={notes}
                    maxLength={MAX_NOTES_LENGTH}
                    showCount
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    placeholder={t('enter_reschedule_notes')}
                    onChange={(e) => setNotes(e.target.value)}
                    className={classes.input}
                />
            </div>
        );
    };

    const renderListItem = (item: RescheduleConsignment & { id: string }, idx: number) => {
        return (
            <div className={classes.itemBox} key={item.id}>
                <span className={classes.serial}>{idx + 1}</span>
                <span className={classes.reference}>{item.reference_number}</span>
                <CloseOutlined
                    style={{ color: 'red' }}
                    onClick={() => setList(list.filter((listItem) => listItem.id !== item.id))}
                    className={classes.icon}
                />
            </div>
        );
    };

    const renderList = () => {
        return (
            <>
                <div className={classes.title}>
                    {t('consignments')}
                </div>
                <div className={classes.columnRow}>
                    <span className={classes.serialTitle}>{t('serialNumber')}</span>
                    <span className={classes.referenceTitle}>{t('ref_number')}</span>
                    <span className={classes.actionTitle}>{t('action')}</span>
                </div>
                <div className={classes.list}>
                    {list.map(renderListItem)}
                </div>
            </>
        );
    };

    const renderSelected = () => {
        return (
            <div>
                {renderType()}
                {renderDate()}
                {renderSlot()}
                {renderReason()}
                {renderNotes()}
                {renderList()}
            </div>
        );
    };

    const onFileReceived = async (file: Blob) => {
        try {
            const sheets: any[] = await Helper.handleXLSXFile(file, bulkHeaders, 1);
            const rows = (sheets[0] || [])
                .map(toBulkRescheduleRow)
                .filter((row: BulkRescheduleRow) => row.consignmentNumber);
            setBulkRows(rows);
        } catch (error) {
            message.error((error as Error).message);
        }
    };

    const handleUploadChange = (info: UploadChangeParam) => {
        if (info.file.status === 'done') {
            onFileReceived(info.file.originFileObj as Blob);
        }
    };

    const customRequest = ({ onSuccess, file }: any) => {
        setTimeout(() => {
            onSuccess(null, file);
        }, 100);
    };

    const downloadSample = () => {
        const slotColumns = allowCustomTimeslot
            ? { slot_start: '1000', slot_end: '1200' }
            : { slot_code: deliveryTimeslots[0]?.code || '' };
        const sampleRow = {
            reference_number: 'CN123456',
            reschedule_date: moment().add(1, 'day').format(BULK_DATE_FORMAT),
            ...slotColumns,
            notes: '',
        };
        Helper.downloadXLSXFile([sampleRow], bulkHeaders, 'bulk_reschedule_sample.xlsx');
    };

    const renderBulk = () => {
        return (
            <div className={classes.bulkOps}>
                <div className={classes.uploadBox}>
                    <Dragger
                        multiple={false}
                        accept=".xlsx, .xls, .csv"
                        name="file"
                        onChange={handleUploadChange}
                        customRequest={customRequest}
                        showUploadList={false}
                    >
                        <div>
                            <div className={classes.uploadText}>
                                <UploadOutlined className={classes.closeIcon} />
                                {t('reschedule_upload_text')}
                            </div>
                            <p className={classes.uploadHint}>
                                {t('drag_and_drop_text')}
                            </p>
                            {bulkRows.length ? (
                                <p className={classes.uploadHint}>
                                    {t('reschedule_rows_found', { count: bulkRows.length })}
                                </p>
                            ) : null}
                        </div>
                    </Dragger>
                </div>
                <a className={classes.downloadSample} onClick={downloadSample}>
                    <DownloadOutlined
                        size={14}
                        style={{
                            marginRight: 8,
                            fontWeight: 'bold',
                        }}
                    />
                    {t('download_sample_file')}
                </a>
            </div>
        );
    };

    const renderHeader = () => {
        return (
            <div className={classes.header}>
                <div>
                    <Cross
                        onClick={() => onClose(false)}
                        alt="close"
                        className={classes.closeIcon}
                    />
                    <span>
                        {t(action === 'BULK' ? 'reschedule_bulk_upload' : 'reschedule')}
                    </span>
                </div>
                <Button
                    type="primary"
                    loading={submitting}
                    onClick={handleSubmit}
                    disabled={isDisabled()}
                >
                    {t('submit')}
                </Button>
            </div>
        );
    };

    return (
        <Drawer
            title={renderHeader()}
            visible
            width={isMobile ? '100%' : '30%'}
            className={classes.main}
            onClose={() => onClose(false)}
            closable={false}
        >
            {action === 'BULK' ? renderBulk() : renderSelected()}
        </Drawer>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    return {
        rescheduleConfig: state.master?.customer_reschedule_config || EMPTY_RESCHEDULE_CONFIG,
        timezone: state.master?.config?.timezone,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default withStyles(cancellationStyles)(GenericHoc(hocConfig)(RescheduleModal));
